import { faUser } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { ReactNode } from 'react';

import './Identity.scss';
import { DraftUser } from '~/types/User';
import { UserIdentityFragment } from '~/graphqlTypes/UserIdentityFragment';

type InterfaceMode = 'node' | 'text';

type IdentityParts = 'givenName' | 'familyName' | 'emailAddress' | 'icon';

export interface IdentityProps {
  className?: string;
  /**
   * The User object.
   */
  value: UserIdentityFragment | DraftUser;
  /**
   * Props to pass along to the component, along with attributes for the parent DOM element to acquire.
   *
   * TODO: Disentangle these?
   */
  propsAndAttributes?: Record<string, any>;
  mode?: InterfaceMode;
  isQueuedForDeletion?: boolean;
  /**
   * Which identity parts to include in the output.
   */
  include?: IdentityParts[];
}

function _IdentityWrapper( wrapperProps: Record<string, any> ) {
  let element;

  if ( wrapperProps.isQueuedForDeletion ) {
    element = 'del';
  } else {
    element = 'span';
  }

  const attributes: Record<string, any> = {
    'data-testid': 'ops-identity',
    ...wrapperProps,
    'className': `ops-identity${wrapperProps.className ? ` ${wrapperProps.className}` : ''}`,
    'data-is-deactivated': wrapperProps.isQueuedForDeletion ? true : undefined,
  };
  delete attributes.isQueuedForDeletion;

  return React.createElement( element, attributes );
}

/**
 * Formats a user object to a string of the form “[first name] [last name] <[email address]>”.
 */
export const Identity = ( props: IdentityProps ) => {
  const { className, value, propsAndAttributes, include = ['givenName', 'familyName', 'emailAddress'] } = props;

  const Wrapper = ( wrapperProps: Record<string, any> ) => _IdentityWrapper( { className, ...wrapperProps } );

  if ( !value ) {
    return null;
  }

  const { id, firstName: givenName, lastName: familyName, emailAddress } = value;

  const showGivenName = Boolean( givenName ) && include.includes( 'givenName' );
  const showFamilyName = Boolean( familyName ) && include.includes( 'familyName' );
  const showGivenNameOnly = showGivenName && !showFamilyName;
  const showFamilyNameOnly = showFamilyName && !showGivenName;
  const showName = showGivenName || showFamilyName;
  const showFullName = showGivenName && showFamilyName;
  const showEmailAddress = Boolean( emailAddress ) && include.includes( 'emailAddress' );
  const showIcon = include.includes( 'icon' );

  return (
    <Wrapper { ...propsAndAttributes }>
      <a className="ops-identity__name" href={ `/user/${id}` } title="View profile" aria-label="View profile">
        <span className="ops-identity__icon">
          { showIcon ? (
            <>
              <FontAwesomeIcon icon={ faUser } title="👤" />
              { showName ? ' ' : null }
            </>
          ) : null }
        </span>
        { showFullName ? (
          <span className="ops-identity__full-name">
            <span className="ops-identity__given-name">{ givenName }</span>{ ' ' }
            <span className="ops-identity__family-name">{ familyName }</span>
          </span>
        ) : null }
        { showGivenNameOnly ? <span className="ops-identity__given-name">{ givenName }</span> : null }
        { showFamilyNameOnly ? <span className="ops-identity__family-name">{ familyName }</span> : null }
        { showName && showEmailAddress ? ' ' : null }
        { showEmailAddress ? (
          <span className="ops-identity__email">
            &lt;<code className="ops-identity__email-address">{ emailAddress }</code>&gt;
          </span>
        ) : null }
      </a>
    </Wrapper>
  );
};

Identity.displayName = 'Identity';

/**
 * Formats a user object to a string of the form “[first name] [last name] <[email address]>”.
 *
 * @param value - The object
 * @param propsAndAttributesOrMode - Props/Attributes for the wrapping element to acquire. If a String, then this serves as the mode param.
 * @param mode - Whether to output as a node (<Identity />) or as text.
 */
export function formatIdentity( value: UserIdentityFragment | DraftUser | null, mode: 'text' ): string;
export function formatIdentity( value: UserIdentityFragment | DraftUser | null, mode?: 'node' ): ReactNode;
export function formatIdentity( value: UserIdentityFragment | DraftUser | null, propsAndAttributesOrMode: IdentityProps['propsAndAttributes'], mode: 'text' ): string;
export function formatIdentity( value: UserIdentityFragment | DraftUser | null, propsAndAttributesOrMode: IdentityProps['propsAndAttributes'], mode?: 'node' ): ReactNode;
export function formatIdentity( value: UserIdentityFragment | DraftUser | null, propsAndAttributesOrMode?: IdentityProps | IdentityProps['propsAndAttributes'] | InterfaceMode, mode: InterfaceMode = 'node' ) {
  if ( !value ) {
    return null;
  }

  const { firstName, lastName, emailAddress, id } = value;

  if ( propsAndAttributesOrMode === 'node' || propsAndAttributesOrMode === 'text' ) {
    mode = propsAndAttributesOrMode;
  }

  if ( mode === 'node' ) {
    if ( !id ) {
      throw new TypeError(
        `formatIdentity#value (the user object) must have an \`id\` property when mode is \`node\`.` +
          ` Got '${id}' of type ${typeof id}; expected a truthy value.` +
          ` Make sure you’re asking for it in the relevant GraphQL request.`,
      );
    }

    return <Identity { ...{ value, propsAndAttributes: propsAndAttributesOrMode as IdentityProps } } />;
  }

  if ( mode === 'text' ) {
    return `${firstName} ${lastName} <${emailAddress}>`;
  }

  throw new Error( 'formatIdentity#mode must be the String `node` or `string`' );
}
